// Objective.tsx
import React, { useEffect, useState } from "react";
import Card from "./_Card";
import Stepper from "../../../components/profile/Stepper";
import Select from "../../../components/ui/Select";
import Textarea from "../../../components/ui/Textarea";
import Button from "../../../components/ui/Button";
import ProfileSidebar from "../../../components/reusable components/ProfileSidebar";
import { useNavigate } from "react-router-dom";
import {
  getProfileObjective,
  putProfileObjective,
  ProfileObjectiveResponse,
  getGrantWriterDetails,
} from "../../../api/profile";
import { getSectors, getBeneficiaries, getSdgs, Option } from "../../../lib/api";
import toast from "react-hot-toast";
import Input from "../../../components/ui/Input";
import { MultiSelect } from "../../../components/ui/MultiSelect";
import ChipMultiSelect from "../../../components/ui/ChipMultiSelect";
import { useAuth } from "../../../contexts/AuthContext";
import { FOCUS_THEME_GROUPS } from "../../../features/onboarding/constants";
import {
  resolveOnboardingRecord,
  updateOnboardingDraft,
} from "../../../features/onboarding/service";
import { valueExistsInOptions } from "./selectHelpers";

const ArrowRight = () => <span className="material-icons">arrow_right_alt</span>;

type ObjectiveForm = {
  sector: string;
  beneficiary: string;
  sdg: string;
  mission: string;
  objective: string;
  history: string;
  dailyActivities: string;
  organizationSize: string;
  problemStatement: string;
  affectedPeople: string;
  needEvidence: string;
  directBeneficiaries: string;
  annualSupportCount: string;
  selectionProcess: string;
  // Project Summary
  projectTitle: string;
  projectOneLine: string;
  projectDetailedSummary: string;
  // Objectives & Outcomes
  projectObjectives: string[];
  projectOutcomes: string[];
  outcomeTimescale: string[];
  // Activities & Delivery
  keyActivities: string;
  deliveryTimeline: string;
  deliveryLocation: string;
  // Budget & Resources
  totalProjectCost: string;
  amountRequested: string;
  budgetBreakdown: string;
  otherFunding: string;
  // Impact & Evaluation
  successIndicators: string;
  dataCollectionMethods: string[];
  longTermImpact: string;
  // Safeguarding & Risk
  safeguardingApproach: string;
  inclusionApproach: string;
  keyRisks: string;
  // Track Record
  previousProjects: string;
  partnerships: string;
  testimonials: string;
  // Sustainability
  postGrantPlan: string;
};

export interface GrantWriterData {
  outcome_timescale: string[];
  data_collection_methods: string[];
}

const parseObjective = (o: unknown) => {
  if (typeof o === "string") {
    return { sector: "", beneficiary: "", sdg: "", mission: "", objectiveText: o, history: "" };
  }
  if (o && typeof o === "object") {
    const obj = o as Record<string, any>;
    return {
      sector: obj.area_focus ?? obj.sector ?? "",
      beneficiary: obj.beneficiary_groups ?? obj.beneficiary ?? "",
      sdg: obj.sustainable_development_goal ?? obj.sdg ?? "",
      mission: obj.mission ?? "",
      objectiveText: obj.objective ?? obj.description ?? "",
      history: obj.organisation_history ?? obj.history ?? "",
    };
  }
  return { sector: "", beneficiary: "", sdg: "", mission: "", objectiveText: "", history: "" };
};

const toForm = (api: ProfileObjectiveResponse): ObjectiveForm => {
  const parsed = parseObjective(api.objective);
  // console.log("API Response:", api);

  // Helper to safely extract nested objective data
  const obj =
    api.objective && typeof api.objective === "object" ? (api.objective as Record<string, any>) : {};

  return {
    // Mission & Focus
    sector: parsed.sector || api.sector || "",
    beneficiary: parsed.beneficiary || "",
    sdg: parsed.sdg || "",
    mission: parsed.mission || api.project_description || "",
    objective: parsed.objectiveText || "",
    history: api.bio || parsed.history || "",

    // Organisation Overview
    dailyActivities: api.org_activities_summary ?? "",
    organizationSize: obj.organisation_profile?.organisation_size ?? "",

    // Problem / Need
    problemStatement: api.problem_statement ?? "",
    affectedPeople: api.who_is_affected ?? "",
    needEvidence: api.evidence_of_need ?? "",

    // Beneficiaries
    directBeneficiaries: api.beneficiaries_description ?? "",
    annualSupportCount: api.beneficiaries_number ? String(api.beneficiaries_number) : "",
    selectionProcess: api.beneficiary_selection_method ?? "",

    // Project Summary
    projectTitle: obj.project?.title ?? "",
    projectOneLine: obj.project?.one_liner ?? "",
    projectDetailedSummary: api.project_description ?? "",

    // Objectives & Outcomes
    projectObjectives: [""], // future enhancement
    projectOutcomes: Array.isArray(obj.project?.expected_outcomes) ? obj.project.expected_outcomes : [""],
    outcomeTimescale: Array.isArray(api.outcome_timescale) ? api.outcome_timescale : [],

    // Activities & Delivery
    keyActivities: obj.project?.key_activities ?? "",
    deliveryTimeline: obj.project?.delivery_timeline ?? "",
    deliveryLocation: obj.project?.delivery_location ?? "",

    // Budget & Resources
    totalProjectCost: obj.budget?.total_cost ?? "",
    amountRequested: obj.budget?.amount_requested ?? "",
    budgetBreakdown: obj.budget?.breakdown ?? "",
    otherFunding: obj.budget?.other_sources ?? "",

    // Impact & Evaluation
    successIndicators: api.success_indicators ?? "",
    dataCollectionMethods: Array.isArray(obj.monitoring.data_collection_methods)
      ? obj.monitoring.data_collection_methods
      : [],
    longTermImpact: api.long_term_impact ?? "",

    // Safeguarding & Risk
    safeguardingApproach: api.safeguarding_approach ?? "",
    inclusionApproach: api.equality_inclusion_approach ?? "",
    keyRisks: obj.risks ?? "",

    // Track Record
    previousProjects: obj.track_record?.previous_projects ?? "",
    partnerships: obj.track_record?.partners ?? "",
    testimonials: obj.track_record?.testimonials ?? "",

    // Sustainability
    postGrantPlan: obj.sustainability?.post_grant_plan ?? "",
  };
};

export default function Objective() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sectorOpts, setSectorOpts] = useState<Option[]>([]);
  const [grantWriterDetail, setGrantWriterDetail] = useState<GrantWriterData | null>(null);
  const [beneficiaryOpts, setBeneficiaryOpts] = useState<Option[]>([]);
  const [sdgOpts, setSdgOpts] = useState<Option[]>([]);
  const [focusThemes, setFocusThemes] = useState<string[]>([]);
  const [primaryBeneficiaries, setPrimaryBeneficiaries] = useState<string[]>([]);
  const [form, setForm] = useState<ObjectiveForm>({
    sector: "",
    beneficiary: "",
    sdg: "",
    mission: "",
    objective: "",
    history: "",
    dailyActivities: "",
    organizationSize: "",
    problemStatement: "",
    affectedPeople: "",
    needEvidence: "",
    directBeneficiaries: "",
    annualSupportCount: "",
    selectionProcess: "",
    projectTitle: "",
    projectOneLine: "",
    projectDetailedSummary: "",
    projectObjectives: [""],
    projectOutcomes: [""],
    outcomeTimescale: [],
    keyActivities: "",
    deliveryTimeline: "",
    deliveryLocation: "",
    totalProjectCost: "",
    amountRequested: "",
    budgetBreakdown: "",
    otherFunding: "",
    successIndicators: "",
    dataCollectionMethods: [],
    longTermImpact: "",
    safeguardingApproach: "",
    inclusionApproach: "",
    keyRisks: "",
    previousProjects: "",
    partnerships: "",
    testimonials: "",
    postGrantPlan: "",
  });
  const userKey = user?.uid || user?.email || "anonymous";

  const sanitizeNonNegativeNumber = (val: string) => {
    const v = (val ?? "").toString();
    if (v === "" || v === "-") return "";
    return v.replace(/-/g, "");
  };

  const preventMinusKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "-") e.preventDefault();
  };

  const onNonNegativeChange =
    (key: "totalProjectCost" | "amountRequested") => (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = sanitizeNonNegativeNumber(e.target.value);
      setForm((s) => ({ ...s, [key]: next }));
    };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getProfileObjective();
        if (active) setForm(toForm(data));
      } catch (e: any) {
        if (active) setError(e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.response?.data?.detail|| "Failed to load objective");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const record = await resolveOnboardingRecord(userKey);
        if (!active) return;
        setForm((current) => ({
          ...current,
          mission: current.mission || record.data.whatWeDo,
        }));
        setFocusThemes(record.data.focusThemes);
        setPrimaryBeneficiaries(record.data.primaryBeneficiaries);
      } catch {
      }
    })();
    return () => {
      active = false;
    };
  }, [userKey]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [s, b, g, d] = await Promise.all([
          getSectors(),
          getBeneficiaries(),
          getSdgs(),
          getGrantWriterDetails(),
        ]);
        if (!active) return;
        setSectorOpts(s);
        setBeneficiaryOpts(b);
        setSdgOpts(g);
        setGrantWriterDetail(d);

        setForm((prev) => {
          const next = { ...prev };
          if (!next.sector) {
            next.sector = "";
          } else if (!valueExistsInOptions(next.sector, s)) {
            next.sector = "";
          }

          if (!next.beneficiary) {
            next.beneficiary = "";
          } else if (!valueExistsInOptions(next.beneficiary, b)) {
            next.beneficiary = "";
          }

          if (next.sdg) {
            const byCode = g.find((o) => o.value === next.sdg);
            const byLabel = g.find((o) => o.label === next.sdg);
            if (byCode) {
              next.sdg = byCode.value;
            } else if (byLabel) {
              next.sdg = byLabel.value;
            } else {
              next.sdg = "";
            }
          } else {
            next.sdg = "";
          }
          return next;
        });
      } catch (e: any) {
        if (active) {
          const msg = e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.response?.data?.detail|| "Failed to load objective metadata";
          toast.error(msg);
        }
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const handleNextObjective = async () => {
    await handleSave();
    navigate("/dashboard/profile/team");
  };
  // console.log(grantWriterDetail);

  const buildPayload = () => ({
    objective: {
      // Core fields
      mission: form.mission,
      objective: form.objective,
      area_focus: form.sector,
      beneficiary_groups: form.beneficiary,
      organisation_history: form.history,
      sustainable_development_goal: form.sdg,
      onboarding_profile: {
        what_we_do: form.mission,
        focus_themes: focusThemes,
        primary_beneficiaries: primaryBeneficiaries,
      },

      // Organisation Profile
      organisation_profile: {
        organisation_size: form.organizationSize,
      },

      // Project Details
      project: {
        title: form.projectTitle,
        one_liner: form.projectOneLine,
        expected_outcomes: form.projectOutcomes.filter((o) => o.trim() !== ""),
        key_activities: form.keyActivities,
        delivery_timeline: form.deliveryTimeline,
        delivery_location: form.deliveryLocation,
      },

      // Budget
      budget: {
        total_cost: sanitizeNonNegativeNumber(form.totalProjectCost),
        amount_requested: sanitizeNonNegativeNumber(form.amountRequested),
        breakdown: form.budgetBreakdown,
        other_sources: form.otherFunding,
      },

      // Monitoring
      monitoring: {
        data_collection_methods: form.dataCollectionMethods,
      },

      // Risks
      risks: form.keyRisks,

      // Track Record
      track_record: {
        previous_projects: form.previousProjects,
        partners: form.partnerships,
        testimonials: form.testimonials,
      },

      // Sustainability
      sustainability: {
        post_grant_plan: form.postGrantPlan,
      },
    },

    // Top-level fields
    project_description: form.mission,
    bio: form.history,
    sector: form.sector,
    org_activities_summary: form.dailyActivities,

    // Problem Statement
    problem_statement: form.problemStatement,
    who_is_affected: form.affectedPeople,
    evidence_of_need: form.needEvidence,

    // Beneficiaries
    beneficiaries_description: form.directBeneficiaries,
    beneficiaries_number: Number(form.annualSupportCount) || 0,
    beneficiary_selection_method: form.selectionProcess,

    // Impact & Evaluation
    success_indicators: form.successIndicators,
    outcome_timescale: form.outcomeTimescale,
    long_term_impact: form.longTermImpact,

    // Safeguarding
    safeguarding_approach: form.safeguardingApproach,
    equality_inclusion_approach: form.inclusionApproach,

    // Data collection methods (top-level as well for backward compatibility)
    data_collection_methods: form.dataCollectionMethods,
  });

  const [saving, setSaving] = useState(false);
  const safeSectorValue = valueExistsInOptions(form.sector, sectorOpts) ? form.sector : "";
  const safeBeneficiaryValue = valueExistsInOptions(form.beneficiary, beneficiaryOpts)
    ? form.beneficiary
    : "";
  const safeSdgValue = valueExistsInOptions(form.sdg, sdgOpts) ? form.sdg : "";

  const handleSave = async () => {
    try {
      setSaving(true);
      await putProfileObjective(buildPayload());
      updateOnboardingDraft(userKey, {
        whatWeDo: form.mission,
        focusThemes,
        primaryBeneficiaries,
      });
      toast.success("Objective saved");
    } catch (e: any) {
      const msg = e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.response?.data?.detail || "Failed to save objective";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const addObjective = () => {
    setForm((s) => ({ ...s, projectObjectives: [...s.projectObjectives, ""] }));
  };

  const removeObjective = (index: number) => {
    setForm((s) => ({
      ...s,
      projectObjectives: s.projectObjectives.filter((_, i) => i !== index),
    }));
  };

  const updateObjective = (index: number, value: string) => {
    setForm((s) => ({
      ...s,
      projectObjectives: s.projectObjectives.map((obj, i) => (i === index ? value : obj)),
    }));
  };

  const addOutcome = () => {
    setForm((s) => ({ ...s, projectOutcomes: [...s.projectOutcomes, ""] }));
  };

  const removeOutcome = (index: number) => {
    setForm((s) => ({
      ...s,
      projectOutcomes: s.projectOutcomes.filter((_, i) => i !== index),
    }));
  };

  const updateOutcome = (index: number, value: string) => {
    setForm((s) => ({
      ...s,
      projectOutcomes: s.projectOutcomes.map((out, i) => (i === index ? value : out)),
    }));
  };

  const toggleTimescale = (value: string) => {
    setForm((s) => ({
      ...s,
      outcomeTimescale: s.outcomeTimescale.includes(value)
        ? s.outcomeTimescale.filter((t) => t !== value)
        : [...s.outcomeTimescale, value],
    }));
  };

  const toggleDataCollection = (value: string) => {
    setForm((s) => ({
      ...s,
      dataCollectionMethods: s.dataCollectionMethods.includes(value)
        ? s.dataCollectionMethods.filter((m) => m !== value)
        : [...s.dataCollectionMethods, value],
    }));
  };

  return (
    <div className="flex min-h-screen gap-5 px-3">
      <ProfileSidebar isProfileRoute={true} />
      <div className="flex-1 min-w-0 overflow-y-auto">
        <div className="h-full">
          <Card>
            <div className="px-6 py-5">
              <Stepper active="objective" />
            </div>

            {loading && <div className="px-6 -mt-2 pb-2 text-sm text-[#6b7280]">Loading objective…</div>}
            {/* {error && <div className="px-6 -mt-2 pb-2 text-sm text-red-600">{error}</div>} */}

            <div className="px-6 pb-6">
              <div className="mb-8 rounded-2xl border border-[#E5E7EB] bg-[#F8FAFC] p-4">
                <div className="text-[18px] font-semibold text-[#111827]">
                  Onboarding profile mapping
                </div>
                <div className="mt-1 text-sm text-[#6B7280]">
                  These fields stay in sync with your first-login onboarding answers so you can edit them here later.
                </div>

                <div className="mt-4 space-y-4">
                  <div>
                    <div className="text-sm font-semibold mb-1">What you do</div>
                    <Textarea
                      placeholder="We provide AI tools to help small businesses access funding."
                      value={form.mission}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        setForm((s) => ({ ...s, mission: e.target.value }))
                      }
                    />
                  </div>

                  <div>
                    <div className="text-sm font-semibold mb-1">Focus themes</div>
                    <ChipMultiSelect
                      groups={FOCUS_THEME_GROUPS}
                      value={focusThemes}
                      onChange={setFocusThemes}
                      maxSelected={5}
                      allowCustom
                      customLabel="Add custom focus"
                      placeholder="Choose up to five themes"
                      onLimitHit={(message) => toast.error(message)}
                    />
                  </div>

                  <div>
                    <div className="text-sm font-semibold mb-1">Primary beneficiaries</div>
                    <ChipMultiSelect
                      options={beneficiaryOpts.map((option) => option.label)}
                      value={primaryBeneficiaries}
                      onChange={setPrimaryBeneficiaries}
                      allowCustom
                      customLabel="Add custom beneficiary"
                      placeholder="Choose beneficiaries"
                    />
                  </div>
                </div>
              </div>

              <div className="text-[20px] font-semibold mb-3">Mission & Focus</div>

              <div className="gap-6">
                <div className="gap-4 gap-x-5 items-center grid grid-cols-2">
                  <div>
                    <div className="text-sm font-semibold mb-1">Area of Focus / Sector</div>
                    <Select value={safeSectorValue} onChange={(e) => setForm((s) => ({ ...s, sector: e.target.value }))}>
                      <option value="" disabled>Select sector</option>
                      {sectorOpts.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </Select>
                  </div>

                  <div>
                    <div className="text-sm font-semibold mb-1">Beneficiary Groups</div>
                    <Select
                      value={safeBeneficiaryValue}
                      onChange={(e) => setForm((s) => ({ ...s, beneficiary: e.target.value }))}
                    >
                      <option value="" disabled>Select beneficiary group</option>
                      {beneficiaryOpts.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </Select>
                  </div>

                  <div className="col-span-2">
                    <div className="text-sm font-semibold mb-1">Sustainable Development Goal Alignment</div>
                    <Select value={safeSdgValue} onChange={(e) => setForm((s) => ({ ...s, sdg: e.target.value }))}>
                      <option value="" disabled>Select SDG alignment</option>
                      {sdgOpts.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </Select>
                  </div>
                </div>

                <div className="space-y-4 mt-6">
                  <div className="text-[20px] font-semibold mb-3">Organisation Overview</div>
                  <div>
                    <div className="text-sm font-semibold mb-1">Mission</div>
                    <Textarea
                      placeholder="1-3 sentences. What do you exist to change or improve?"
                      value={form.mission}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        setForm((s) => ({ ...s, mission: e.target.value }))
                      }
                    />
                  </div>

                  <div>
                    <div className="text-sm font-semibold mb-1">Objective</div>
                    <Textarea
                      placeholder="Write your objectives here..."
                      value={form.objective}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        setForm((s) => ({ ...s, objective: e.target.value }))
                      }
                    />
                  </div>

                  <div>
                    <div className="text-sm font-semibold mb-1">Organization History</div>
                    <Textarea
                      placeholder="Tell us a bit about your background and story..."
                      value={form.history}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        setForm((s) => ({ ...s, history: e.target.value }))
                      }
                    />
                  </div>

                  <div>
                    <div className="text-sm font-semibold mb-1">What does your organisation do day-to-day?</div>
                    <Textarea
                      placeholder="Summarise your main services, programmes, or products in simple language."
                      value={form.dailyActivities}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        setForm((s) => ({ ...s, dailyActivities: e.target.value }))
                      }
                    />
                  </div>

                  <div>
                    <div className="text-sm font-semibold mb-1">Organization Size and Team</div>
                    <Textarea
                      placeholder="Include number of staff, volunteers, key roles, and any lived-experience leadership"
                      value={form.organizationSize}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        setForm((s) => ({ ...s, organizationSize: e.target.value }))
                      }
                    />
                  </div>

                  <div className="text-[20px] font-semibold mb-3 mt-6">Problem / Need Statement</div>

                  <div>
                    <div className="text-sm font-semibold mb-1">What problem or need are you trying to solve?</div>
                    <Textarea
                      placeholder="Describe the challenge in your community or sector and why it matters"
                      value={form.problemStatement}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        setForm((s) => ({ ...s, problemStatement: e.target.value }))
                      }
                    />
                  </div>

                  <div>
                    <div className="text-sm font-semibold mb-1">Who is most affected by this problem?</div>
                    <Textarea
                      placeholder="Age, location, background, and any key characteristics."
                      value={form.affectedPeople}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        setForm((s) => ({ ...s, affectedPeople: e.target.value }))
                      }
                    />
                  </div>

                  <div>
                    <div className="text-sm font-semibold mb-1">What evidence do you have that this need is real?</div>
                    <Textarea
                      placeholder="Local stats, waiting lists, surveys, lived-experience feedback, case studies."
                      value={form.needEvidence}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        setForm((s) => ({ ...s, needEvidence: e.target.value }))
                      }
                    />
                  </div>

                  <div className="text-[20px] font-semibold mb-3 mt-6">Beneficiaries & Target Group</div>

                  <div>
                    <div className="text-sm font-semibold mb-1">Who will directly benefit from your project?</div>
                    <Textarea
                      placeholder="Describe them in human terms, not just numbers."
                      value={form.directBeneficiaries}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        setForm((s) => ({ ...s, directBeneficiaries: e.target.value }))
                      }
                    />
                  </div>

                  <div>
                    <div className="text-sm font-semibold mb-1">Roughly how many people will you support per year?</div>
                    <Input
                      type="number"
                      value={form.annualSupportCount}
                      placeholder=""
                      onChange={(e) => setForm((s) => ({ ...s, annualSupportCount: e.target.value }))}
                    />
                  </div>

                  <div>
                    <div className="text-sm font-semibold mb-1">How will you find and select people to support?</div>
                    <Textarea
                      placeholder="Referral routes, outreach, criteria, or open access."
                      value={form.selectionProcess}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        setForm((s) => ({ ...s, selectionProcess: e.target.value }))
                      }
                    />
                  </div>
                </div>
              </div>

              {/* PROJECT SUMMARY SECTION */}
              <div className="text-[20px] font-semibold mb-3 mt-8">Project Summary</div>

              <div className="space-y-4">
                <div>
                  <div className="text-sm font-semibold mb-1">Project Title</div>
                  <Input
                    type="text"
                    value={form.projectTitle}
                    placeholder="What is the name of this project or programme?"
                    onChange={(e) => setForm((s) => ({ ...s, projectTitle: e.target.value }))}
                  />
                </div>

                <div>
                  <div className="text-sm font-semibold mb-1">One-line Summary</div>
                  <Input
                    type="text"
                    value={form.projectOneLine}
                    placeholder="Plain English. Example: 'We run free weekly STEM clubs for 50 young people in rural Georgia.'"
                    onChange={(e) => setForm((s) => ({ ...s, projectOneLine: e.target.value }))}
                  />
                </div>

                <div>
                  <div className="text-sm font-semibold mb-1">Detailed Project Summary</div>
                  <Textarea
                    placeholder="What you'll do, who with, where, when. 1–3 short paragraphs."
                    value={form.projectDetailedSummary}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setForm((s) => ({ ...s, projectDetailedSummary: e.target.value }))
                    }
                  />
                </div>
              </div>

              {/* OBJECTIVES & OUTCOMES */}
              <div className="text-[20px] font-semibold mb-3 mt-8">Objectives & Outcomes</div>

              <div className="space-y-4">
                <div>
                  <div className="text-sm font-semibold mb-1">Expected Outcomes</div>
                  <div className="text-xs text-gray-500 mb-2">
                    Describe the change, not the activity. Example: 'Young people report feeling more confident applying
                    for STEM courses.'
                  </div>
                  {form.projectOutcomes.map((outcome, index) => (
                    <div key={index} className="flex gap-2 mb-2">
                      <Input
                        type="text"
                        value={outcome}
                        placeholder={`Outcome content`}
                        onChange={(e) => updateOutcome(index, e.target.value)}
                      />
                    </div>
                  ))}
                </div>

                <div>
                  <div className="text-sm font-semibold mb-1">When do you expect to see these outcomes?</div>
                  <div className="space-y-2">
                    <MultiSelect
                      options={grantWriterDetail?.outcome_timescale || []}
                      value={form.outcomeTimescale}
                      onChange={(selected: any) => {
                        // console.log("Selected:", selected);
                        setForm((s) => ({
                          ...s,
                          outcomeTimescale: selected,
                        }));
                      }}
                      placeholder="Select outcome timescale..."
                    />
                  </div>
                </div>
              </div>

              {/* ACTIVITIES & DELIVERY */}
              <div className="text-[20px] font-semibold mb-3 mt-8">Activities & Delivery Plan</div>

              <div className="space-y-4">
                <div>
                  <div className="text-sm font-semibold mb-1">Key Activities</div>
                  <Textarea
                    placeholder="Workshops, sessions, training, mentoring, events, online support, etc."
                    value={form.keyActivities}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setForm((s) => ({ ...s, keyActivities: e.target.value }))
                    }
                  />
                </div>

                <div>
                  <div className="text-sm font-semibold mb-1">Delivery Timeline</div>
                  <Textarea
                    placeholder="Month-by-month or phase-by-phase. Example: 'Months 1–2: recruitment and setup…'"
                    value={form.deliveryTimeline}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setForm((s) => ({ ...s, deliveryTimeline: e.target.value }))
                    }
                  />
                </div>

                <div>
                  <div className="text-sm font-semibold mb-1">Delivery Location</div>
                  <Textarea
                    placeholder="Venues, online, neighbourhoods, towns, or regions."
                    value={form.deliveryLocation}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setForm((s) => ({ ...s, deliveryLocation: e.target.value }))
                    }
                  />
                </div>
              </div>

              {/* BUDGET & RESOURCES */}
              <div className="text-[20px] font-semibold mb-3 mt-8">Budget & Resources</div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm font-semibold mb-1">Total Project Cost</div>
                    <Input
                      type="number"
                      min={0}
                      value={form.totalProjectCost}
                      placeholder="£"
                      onKeyDown={preventMinusKey}
                      onChange={onNonNegativeChange("totalProjectCost")}
                    />
                  </div>

                  <div>
                    <div className="text-sm font-semibold mb-1">Amount Requested from Grants</div>
                    <Input
                      type="number"
                      min={0}
                      value={form.amountRequested}
                      placeholder="£"
                      onKeyDown={preventMinusKey}
                      onChange={onNonNegativeChange("amountRequested")}
                    />
                  </div>
                </div>

                <div>
                  <div className="text-sm font-semibold mb-1">Budget Breakdown</div>
                  <Textarea
                    placeholder="List items and amounts. Example: 'Staff: £20,000; Venue: £5,000; Equipment: £3,000…'"
                    value={form.budgetBreakdown}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setForm((s) => ({ ...s, budgetBreakdown: e.target.value }))
                    }
                  />
                </div>

                <div>
                  <div className="text-sm font-semibold mb-1">Other Funding Sources</div>
                  <Textarea
                    placeholder="List confirmed funds, pending applications, in-kind support, and your own contribution."
                    value={form.otherFunding}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setForm((s) => ({ ...s, otherFunding: e.target.value }))
                    }
                  />
                </div>
              </div>

              {/* IMPACT & EVALUATION */}
              <div className="text-[20px] font-semibold mb-3 mt-8">Impact & Evaluation</div>

              <div className="space-y-4">
                <div>
                  <div className="text-sm font-semibold mb-1">Success Indicators</div>
                  <Textarea
                    placeholder="What will you measure? Example: attendance, feedback, surveys, skills tests."
                    value={form.successIndicators}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setForm((s) => ({ ...s, successIndicators: e.target.value }))
                    }
                  />
                </div>

                <div>
                  <div className="text-sm font-semibold mb-1">Data Collection Methods</div>
                  <div className="space-y-2">
                    <MultiSelect
                      options={grantWriterDetail?.data_collection_methods || []}
                      value={form.dataCollectionMethods}
                      onChange={(selected) => {
                        // console.log("Selected:", selected);
                        setForm((s) => ({
                          ...s,
                          dataCollectionMethods: selected,
                        }));
                      }}
                      placeholder="Select outcome timescale..."
                    />
                  </div>
                </div>

                <div>
                  <div className="text-sm font-semibold mb-1">Long-term Impact</div>
                  <Textarea
                    placeholder="What long-term impact do you hope to see?"
                    value={form.longTermImpact}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setForm((s) => ({ ...s, longTermImpact: e.target.value }))
                    }
                  />
                </div>
              </div>

              {/* SAFEGUARDING & RISK */}
              <div className="text-[20px] font-semibold mb-3 mt-8">Safeguarding, Inclusion & Risk</div>

              <div className="space-y-4">
                <div>
                  <div className="text-sm font-semibold mb-1">Safeguarding Approach</div>
                  <Textarea
                    placeholder="Policies, DBS checks, training, supervision, risk assessments."
                    value={form.safeguardingApproach}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setForm((s) => ({ ...s, safeguardingApproach: e.target.value }))
                    }
                  />
                </div>

                <div>
                  <div className="text-sm font-semibold mb-1">Equality & Inclusion Approach</div>
                  <Textarea
                    placeholder="How do you ensure your work is inclusive and accessible?"
                    value={form.inclusionApproach}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setForm((s) => ({ ...s, inclusionApproach: e.target.value }))
                    }
                  />
                </div>

                <div>
                  <div className="text-sm font-semibold mb-1">Key Risks and Mitigation</div>
                  <Textarea
                    placeholder="Include practical risks (staffing, funding, engagement) and how you'll reduce them."
                    value={form.keyRisks}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setForm((s) => ({ ...s, keyRisks: e.target.value }))
                    }
                  />
                </div>
              </div>

              {/* TRACK RECORD & PARTNERSHIPS */}
              <div className="text-[20px] font-semibold mb-3 mt-8">Track Record & Partnerships</div>

              <div className="space-y-4">
                <div>
                  <div className="text-sm font-semibold mb-1">Previous Projects</div>
                  <Textarea
                    placeholder="Briefly describe any relevant past work and results."
                    value={form.previousProjects}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setForm((s) => ({ ...s, previousProjects: e.target.value }))
                    }
                  />
                </div>

                <div>
                  <div className="text-sm font-semibold mb-1">Key Partners or Collaborators</div>
                  <Textarea
                    placeholder="Schools, charities, councils, networks, advisors."
                    value={form.partnerships}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setForm((s) => ({ ...s, partnerships: e.target.value }))
                    }
                  />
                </div>

                <div>
                  <div className="text-sm font-semibold mb-1">Testimonials or Quotes</div>
                  <Textarea
                    placeholder="Any quotes, testimonials, or short stories we can use?"
                    value={form.testimonials}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setForm((s) => ({ ...s, testimonials: e.target.value }))
                    }
                  />
                </div>
              </div>

              {/* SUSTAINABILITY */}
              <div className="text-[20px] font-semibold mb-3 mt-8">Sustainability</div>

              <div className="space-y-4">
                <div>
                  <div className="text-sm font-semibold mb-1">Post-Grant Plan</div>
                  <Textarea
                    placeholder="What happens after this grant ends? Do you plan to continue, expand, or find other funding?"
                    value={form.postGrantPlan}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setForm((s) => ({ ...s, postGrantPlan: e.target.value }))
                    }
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <Button className="min-w-[96px]" disabled={saving} onClick={handleSave}>
                  {saving ? "Saving…" : "Save"}
                </Button>
                <Button className="min-w-[96px] gap-2" onClick={handleNextObjective}>
                  Next <ArrowRight />
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
